/**
 * CompletionScreen Component
 *
 * Final screen shown after exit survey completion.
 * Integrates with Prolific for automatic completion tracking.
 *
 * Features:
 * - Detects if participant came from Prolific (via URL params)
 * - Shows Prolific redirect button for Prolific participants
 * - Falls back to manual completion code for other platforms
 * - Saves unique participant ID to database for tracking
 *
 * Prolific Integration:
 * - PROLIFIC_COMPLETION_CODE: Set this to your study's completion code from Prolific
 * - Participants are redirected to Prolific to confirm completion
 */

import { usePlayer } from "@empirica/core/player/classic/react";
import React, { useEffect, useState } from "react";

// ============================================================
// PROLIFIC CONFIGURATION - UPDATE THIS FOR EACH STUDY
// ============================================================
// Get this code from Prolific when you create your study
// Prolific Dashboard → Study → Completion → "Use a URL redirect"
const PROLIFIC_COMPLETION_CODE = "CM6RMXNE";
// ============================================================

export function CompletionScreen() {
  const player = usePlayer();
  const [codeSaved, setCodeSaved] = useState(false);

  // Get Prolific information (captured in callbacks.js from URL params)
  const prolificPid = player.get("dummy_prolific_id");
  const prolificStudyId = player.get("dummy_study_id");
  const prolificSessionId = player.get("dummy_session_id");

  // Check if participant came from Prolific
  const isFromProlific = prolificPid && prolificPid !== "NA";

  // Generate a unique internal tracking code (saved to database)
  const internalTrackingId = `LEARNING-${player.id.slice(-8).toUpperCase()}`;

  // Prolific redirect URL
  const prolificRedirectUrl = `https://app.prolific.com/submissions/complete?cc=${PROLIFIC_COMPLETION_CODE}`;

  useEffect(() => {
    if (!codeSaved) {
      // Save completion data to player record
      player.set("completionTimestamp", new Date().toISOString());
      player.set("studyCompleted", true);
      player.set("exitStatus", "finished"); // Triggers completion tracking in callbacks.js
      player.set("internalTrackingId", internalTrackingId);

      // Save platform-specific info
      if (isFromProlific) {
        player.set("completionPlatform", "prolific");
        player.set("prolificCompletionCode", PROLIFIC_COMPLETION_CODE);
      } else {
        player.set("completionPlatform", "other");
        player.set("completionCode", internalTrackingId);
      }

      setCodeSaved(true);
    }
  }, [player, internalTrackingId, isFromProlific, codeSaved]);

  // Handle Prolific redirect
  const handleProlificRedirect = () => {
    window.location.href = prolificRedirectUrl;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        <div className="bg-white shadow-xl rounded-lg p-8">
          {/* Header */}
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Thank You for Participating!
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              You have successfully completed the study.
            </p>
          </div>

          {/* Prolific Participant Flow */}
          {isFromProlific ? (
            <>
              <div className="bg-purple-50 border-2 border-purple-500 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-semibold text-purple-900 mb-3 text-center">
                  Complete Your Submission
                </h3>
                <p className="text-gray-700 text-center mb-4">
                  Click the button below to return to Prolific and confirm your completion.
                </p>
                <button
                  onClick={handleProlificRedirect}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors flex items-center justify-center gap-2"
                >
                  Complete on Prolific
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </div>

              {/* Fallback manual code */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-600 mb-2">
                  If the button doesn't work, enter this code manually on Prolific:
                </p>
                <div className="bg-white border border-gray-300 rounded p-3 text-center">
                  <p className="text-xl font-mono font-bold text-gray-800 select-all">
                    {PROLIFIC_COMPLETION_CODE}
                  </p>
                </div>
              </div>
            </>
          ) : (
            /* Non-Prolific Participant Flow */
            <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-green-900 mb-3">
                Your Completion Code:
              </h3>
              <div className="bg-white border-2 border-green-600 rounded p-4 text-center">
                <p className="text-3xl font-mono font-bold text-green-700 select-all">
                  {internalTrackingId}
                </p>
              </div>
              <p className="text-sm text-gray-700 mt-4">
                <strong>Important:</strong> Copy this code and paste it into your survey platform to receive payment.
                Keep this window open until you have successfully submitted the code.
              </p>
            </div>
          )}

          {/* Contact Info */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <p className="text-sm text-gray-700">
              If you have any questions or encounter any issues with payment,
              please contact the research team.
              {!isFromProlific && (
                <> Reference your code: <strong>{internalTrackingId}</strong></>
              )}
            </p>
          </div>

          {/* Internal Tracking ID (for researchers) */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400">
              Internal Reference: {internalTrackingId}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
